<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import type { PageData } from './$types';
  import ContentCard from '$lib/components/content/ContentCard.svelte';
  
  interface Props {
    data: PageData;
  }
  
  let { data }: Props = $props();
  
  onMount(() => {
    // Track page view
    if (typeof window !== 'undefined' && window.plausible) {
      window.plausible('Stories Page View', {
        props: { 
          locale: data.locale,
          storiesCount: data.stories.length
        }
      });
    }
  });
  
  function formatDate(dateString: string, locale: string): string {
    const date = new Date(dateString);
    const localeCode = locale.split('-')[0];
    
    return date.toLocaleDateString(localeCode, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  
  function getIndustryIcon(industry: string): string {
    switch (industry.toLowerCase()) {
      case 'technology': return '💻';
      case 'healthcare': return '🏥';
      case 'finance': return '💰';
      case 'education': return '🎓';
      case 'retail': return '🛍️';
      case 'manufacturing': return '🏭';
      case 'consulting': return '💼';
      case 'marketing': return '📈';
      default: return '🏢';
    }
  }
</script>

<svelte:head>
  <title>{data.meta.title} | Heyweek</title>
  <meta name="description" content={data.meta.description} />
  <link rel="canonical" href="https://heyweek.com{data.meta.canonical}" />
  
  <!-- Open Graph -->
  <meta property="og:title" content="{data.meta.title} | Heyweek" />
  <meta property="og:description" content={data.meta.description} />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://heyweek.com{data.meta.canonical}" />
  
  <!-- Twitter Card -->
  <meta name="twitter:title" content="{data.meta.title} | Heyweek" />
  <meta name="twitter:description" content={data.meta.description} />
</svelte:head>

<div class="stories-page">
  <!-- Hero Section -->
  <section class="stories-hero">
    <div class="container">
      <div class="stories-hero__content">
        <h1 class="stories-hero__title">{$_('stories.title')}</h1>
        <p class="stories-hero__subtitle">{$_('stories.subtitle')}</p>
        
        <!-- Stats -->
        <div class="stories-hero__stats">
          <div class="stories-stat">
            <span class="stories-stat__number">{data.stories.length}</span>
            <span class="stories-stat__label">{$_('stories.stats.stories')}</span>
          </div>
          <div class="stories-stat">
            <span class="stories-stat__number">{data.industries.length}</span>
            <span class="stories-stat__label">{$_('stories.stats.industries')}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
  
  <!-- Featured Stories -->
  {#if data.featuredStories.length > 0}
    <section class="featured-stories">
      <div class="container">
        <h2 class="featured-stories__title">{$_('stories.featured.title')}</h2>
        <div class="featured-stories__grid">
          {#each data.featuredStories as story}
            <ContentCard item={story} locale={data.locale} />
          {/each}
        </div>
      </div>
    </section>
  {/if}
  
  <!-- All Stories -->
  <section class="stories-content">
    <div class="container">
      <div class="stories-content__layout">
        <!-- Sidebar -->
        <aside class="stories-sidebar">
          <div class="filter-section">
            <h3 class="filter-section__title">{$_('stories.filters.industries')}</h3>
            <div class="filter-section__options">
              {#each data.industries as industry}
                <div class="industry-item">
                  <span class="industry-item__icon">{getIndustryIcon(industry)}</span>
                  <span class="industry-item__name">{industry}</span>
                  <span class="industry-item__count">
                    {data.stories.filter(s => s.industry === industry).length}
                  </span>
                </div>
              {/each}
            </div>
          </div>
          
          <!-- CTA -->
          <div class="stories-cta">
            <h3 class="stories-cta__title">{$_('stories.cta.title')}</h3>
            <p class="stories-cta__description">{$_('stories.cta.description')}</p>
            <a href="/contact" class="stories-cta__button">
              {$_('stories.cta.button')} →
            </a>
          </div>
        </aside>
        
        <!-- Main Content -->
        <main class="stories-main">
          {#if data.stories.length > 0}
            <div class="customer-stories">
              <div class="customer-stories__header">
                <h2 class="customer-stories__title">{$_('stories.all.title')}</h2>
                <p class="customer-stories__count">
                  {$_('stories.showing', { values: { count: data.stories.length } })}
                </p>
              </div>
              
              <div class="customer-stories__grid">
                {#each data.stories as story}
                  <div class="customer-story">
                    <div class="customer-story__header">
                      <div class="customer-story__meta">
                        <span class="customer-story__industry">
                          {getIndustryIcon(story.industry)} {story.industry}
                        </span>
                        <time class="customer-story__date" datetime={story.date}>
                          {formatDate(story.date, data.locale)}
                        </time>
                      </div>
                    </div>
                    
                    <div class="customer-story__content">
                      <h3 class="customer-story__customer">{story.customer}</h3>
                      <h4 class="customer-story__title">
                        <a href="/stories/{story.slug}" class="customer-story__link">
                          {story.title}
                        </a>
                      </h4>
                      <p class="customer-story__excerpt">{story.excerpt}</p>
                      
                      {#if story.tags.length > 0}
                        <div class="customer-story__tags">
                          {#each story.tags.slice(0, 3) as tag}
                            <span class="customer-story__tag">#{tag}</span>
                          {/each}
                          {#if story.tags.length > 3}
                            <span class="customer-story__tag customer-story__tag--more">
                              +{story.tags.length - 3}
                            </span>
                          {/if}
                        </div>
                      {/if}
                    </div>
                  </div>
                {/each}
              </div>
            </div>
          {:else}
            <div class="stories-empty">
              <div class="stories-empty__icon">💼</div>
              <h3 class="stories-empty__title">{$_('stories.empty.title')}</h3>
              <p class="stories-empty__description">{$_('stories.empty.description')}</p>
            </div>
          {/if}
        </main>
      </div>
    </div>
  </section>
</div>

<style>
  .stories-page {
    min-height: 100vh;
  }
  
  .stories-hero {
    background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
    padding: var(--space-12) 0 var(--space-8);
    border-bottom: 1px solid var(--border-light);
  }
  
  .stories-hero__content {
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .stories-hero__title {
    font-size: var(--font-size-3xl);
    font-weight: var(--font-weight-bold);
    color: var(--text-primary);
    margin-bottom: var(--space-4);
    line-height: 1.2;
  }
  
  .stories-hero__subtitle {
    font-size: var(--font-size-lg);
    color: var(--text-secondary);
    margin-bottom: var(--space-8);
    line-height: 1.5;
  }
  
  .stories-hero__stats {
    display: flex;
    justify-content: center;
    gap: var(--space-8);
  }
  
  .stories-stat {
    text-align: center;
  }
  
  .stories-stat__number {
    display: block;
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    color: var(--brand-primary);
    margin-bottom: var(--space-1);
  }
  
  .stories-stat__label {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .featured-stories {
    padding: var(--space-12) 0;
    background: var(--bg-secondary);
  }
  
  .featured-stories__title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-6);
    text-align: center;
  }
  
  .featured-stories__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: var(--space-6);
  }
  
  .stories-content {
    padding: var(--space-8) 0;
  }
  
  .stories-content__layout {
    display: grid;
    grid-template-columns: 250px 1fr;
    gap: var(--space-8);
    align-items: start;
  }
  
  .stories-sidebar {
    position: sticky;
    top: var(--space-8);
  }
  
  .filter-section {
    background: var(--bg-secondary);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    margin-bottom: var(--space-6);
  }
  
  .filter-section__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-4);
  }
  
  .filter-section__options {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  
  .industry-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    background: var(--bg-primary);
    border-radius: var(--radius-md);
    border: 1px solid var(--border-light);
  }
  
  .industry-item__icon {
    font-size: var(--font-size-base);
  }
  
  .industry-item__name {
    flex: 1;
    font-size: var(--font-size-sm);
    color: var(--text-primary);
  }
  
  .industry-item__count {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
    background: var(--bg-tertiary);
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
  }
  
  .stories-cta {
    background: var(--brand-primary-light);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    text-align: center;
  }
  
  .stories-cta__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--brand-primary-dark);
    margin-bottom: var(--space-3);
  }
  
  .stories-cta__description {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    margin-bottom: var(--space-4);
    line-height: 1.4;
  }
  
  .stories-cta__button {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-2) var(--space-4);
    background: var(--brand-primary);
    color: white;
    text-decoration: none;
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    transition: var(--transition-colors);
  }
  
  .stories-cta__button:hover {
    background: var(--brand-primary-dark);
  }
  
  .customer-stories__header {
    margin-bottom: var(--space-6);
  }
  
  .customer-stories__title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-2);
  }
  
  .customer-stories__count {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }
  
  .customer-stories__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: var(--space-6);
  }
  
  .customer-story {
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    transition: var(--transition-all);
  }
  
  .customer-story:hover {
    border-color: var(--border-medium);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .customer-story__header {
    margin-bottom: var(--space-4);
  }
  
  .customer-story__meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: var(--font-size-sm);
  }
  
  .customer-story__industry {
    color: var(--text-secondary);
    font-weight: var(--font-weight-medium);
  }
  
  .customer-story__date {
    color: var(--text-tertiary);
  }
  
  .customer-story__customer {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-bold);
    color: var(--brand-primary);
    margin-bottom: var(--space-2);
  }
  
  .customer-story__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    line-height: 1.3;
    margin-bottom: var(--space-3);
  }
  
  .customer-story__link {
    color: var(--text-primary);
    text-decoration: none;
    transition: var(--transition-colors);
  }
  
  .customer-story__link:hover {
    color: var(--brand-primary);
  }
  
  .customer-story__excerpt {
    color: var(--text-secondary);
    line-height: 1.5;
    margin-bottom: var(--space-4);
  }
  
  .customer-story__tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1);
  }
  
  .customer-story__tag {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
    background: var(--bg-secondary);
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    border: 1px solid var(--border-light);
  }
  
  .customer-story__tag--more {
    color: var(--brand-primary);
    background: var(--brand-primary-light);
    border-color: var(--brand-primary);
  }
  
  .stories-empty {
    text-align: center;
    padding: var(--space-12) var(--space-4);
  }
  
  .stories-empty__icon {
    font-size: 4rem;
    margin-bottom: var(--space-4);
    opacity: 0.6;
  }
  
  .stories-empty__title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-3);
  }
  
  .stories-empty__description {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    line-height: 1.5;
  }
  
  @media (max-width: 1024px) {
    .stories-content__layout {
      grid-template-columns: 1fr;
      gap: var(--space-6);
    }
    
    .stories-sidebar {
      position: static;
      order: -1;
    }
    
    .filter-section {
      padding: var(--space-4);
    }
  }
  
  @media (max-width: 768px) {
    .stories-hero__title {
      font-size: var(--font-size-xl);
    }
    
    .stories-hero__subtitle {
      font-size: var(--font-size-base);
    }
    
    .stories-hero__stats {
      flex-direction: column;
      gap: var(--space-4);
    }
    
    .featured-stories__grid {
      grid-template-columns: 1fr;
    }
    
    .customer-stories__grid {
      grid-template-columns: 1fr;
    }
    
    .customer-story__meta {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-1);
    }
  }
</style>
